import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useTenantPath } from "@/lib/tenantPath";
import { useRole } from "@/lib/roles";
import { CheckCircle2, Circle, ArrowRight, X, Sparkles } from "lucide-react";

export default function OnboardingChecklist() {
  const { t } = useTranslation("onboarding");
  const tPath = useTenantPath();
  const { canManageOrg } = useRole();
  const [state, setState] = useState(null);
  const [hidden, setHidden] = useState(false);

  const refresh = () => {
    api.get("/organizations/me/onboarding-status")
      .then((r) => setState(r.data))
      .catch(() => setState(null));
  };

  useEffect(() => { if (canManageOrg) refresh(); }, [canManageOrg]);

  useEffect(() => {
    if (!state) return;
    if (state.completed_at || state.dismissed_at) return;
    if (state.progress?.done === state.progress?.total) {
      api.post("/organizations/me/onboarding/complete", {}).then(refresh).catch(() => {});
    }
  }, [state]);

  const dismiss = async () => {
    setHidden(true);
    await api.post("/organizations/me/onboarding/dismiss").catch(() => {});
    refresh();
  };

  if (!canManageOrg || !state || hidden) return null;
  if (state.should_hide_checklist) return null;

  const items = [
    {
      id: "agency",
      label: t("checklist.agency.label"),
      done: state.steps.agency.done,
      hint: state.steps.agency.name || t("checklist.agency.hint"),
      to: tPath("/onboarding?step=1"),
    },
    {
      id: "promoters",
      label: state.steps.promoters.count > 0
        ? t("checklist.promoters.labelDone", { count: state.steps.promoters.count })
        : t("checklist.promoters.labelPending"),
      done: state.steps.promoters.done,
      hint: state.steps.promoters.done ? t("checklist.promoters.hintDone") : t("checklist.promoters.hintPending"),
      to: tPath("/onboarding?step=2"),
    },
    {
      id: "whatsapp",
      label: state.steps.whatsapp.done ? t("checklist.whatsapp.labelDone") : t("checklist.whatsapp.labelPending"),
      done: state.steps.whatsapp.done,
      hint: state.steps.whatsapp.done ? t("checklist.whatsapp.hintDone") : t("checklist.whatsapp.hintPending"),
      to: tPath("/configuracoes/whatsapp"),
    },
    {
      id: "team",
      label: t("checklist.team.label"),
      done: state.steps.team.done,
      hint: state.steps.team.pending_invites > 0
        ? t("checklist.team.hintPending", { count: state.steps.team.pending_invites })
        : t("checklist.team.hintEmpty"),
      to: tPath("/utilizadores"),
    },
  ];

  const doneCount = state.progress?.done ?? 0;
  const total = state.progress?.total ?? 4;
  const pct = Math.round((doneCount / total) * 100);

  return (
    <div data-testid="onboarding-checklist" className="bg-gradient-to-br from-emerald-50 via-white to-white border border-emerald-200 rounded-2xl overflow-hidden shadow-sm">
      <div className="px-5 py-4 flex items-start justify-between gap-3 border-b border-emerald-100/70">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-emerald-600 text-white flex items-center justify-center">
            <Sparkles className="w-4 h-4" />
          </div>
          <div>
            <h3 className="font-display text-lg font-semibold text-zinc-950">{t("checklist.title")}</h3>
            <p className="text-xs text-zinc-600 mt-0.5">
              {doneCount === total
                ? t("checklist.allDone")
                : t("checklist.progress", { done: doneCount, total })}
            </p>
          </div>
        </div>
        <button
          data-testid="onboarding-dismiss"
          onClick={dismiss}
          className="text-zinc-400 hover:text-zinc-700 p-1 rounded-md hover:bg-white/60"
          title={t("checklist.dismiss")}
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="h-1 bg-emerald-100">
        <div className="h-1 bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
      </div>

      <ul className="divide-y divide-emerald-100/60">
        {items.map((it) => (
          <li key={it.id} data-testid={`checklist-item-${it.id}`} className="px-5 py-3 flex items-center gap-3">
            {it.done ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
            ) : (
              <Circle className="w-5 h-5 text-zinc-300 shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className={`text-sm ${it.done ? "text-zinc-500 line-through" : "text-zinc-900 font-medium"}`}>
                {it.label}
              </div>
              <div className="text-xs text-zinc-500 mt-0.5 truncate">{it.hint}</div>
            </div>
            {!it.done && (
              <Link
                to={it.to}
                data-testid={`checklist-cta-${it.id}`}
                className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-900 whitespace-nowrap"
              >
                {t("checklist.continue")} <ArrowRight className="w-3 h-3" />
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
